import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clientService from 'services/clientService';
import budgetService from 'services/budgetService';

const AccountDetailPage = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  
  const [account, setAccount] = useState(null);
  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAccountDetails();
  }, [accountId]);

  const loadAccountDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Cargar todas las órdenes de Performance y filtrar por cuenta
      const ordersResult = await budgetService.getAllInsertionOrders('Performance');
      
      if (ordersResult.success) {
        // Filtrar órdenes de esta cuenta específica
        const accountOrders = ordersResult.orders.filter(order => 
          order.accountId === accountId
        );
        
        if (accountOrders.length > 0) {
          const firstOrder = accountOrders[0];
          
          // Buscar información del cliente por nombre
          const clientsResult = await clientService.getAllClients();
          if (clientsResult.success) {
            const foundClient = clientsResult.clients.find(c => 
              c.ClientName === firstOrder.clientName
            );
            
            if (foundClient) {
              // Cargar las cuentas de este cliente para obtener detalles de la cuenta
              const accountsResult = await clientService.getAccountsByClientId(foundClient.ClientID);
              if (accountsResult.success) {
                const foundAccount = accountsResult.accounts.find(acc => 
                  acc.AccountID === accountId
                );
                
                if (foundAccount) {
                  setAccount(foundAccount);
                  setClient(foundClient);
                } else {
                  setError('No se encontró información detallada de la cuenta');
                }
              }
            } else {
              setError('No se encontró información del cliente');
            }
          }
        } else {
          setError('No se encontraron órdenes para esta cuenta');
        }
        
        setOrders(accountOrders);
      } else {
        setError('Error al cargar las órdenes de inserción');
      }
    } catch (err) {
      console.error('Error loading account details:', err);
      setError('Error al cargar los detalles de la cuenta');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getTotalInvestment = () => {
    return orders.reduce((total, order) => total + (parseFloat(order.grossInvestment) || 0), 0);
  };

  const getActiveOrdersCount = () => {
    return orders.filter(order => order.isActive).length;
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Cargando detalles de la cuenta...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger" className="text-center">
          <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
          {error}
        </Alert>
        <div className="text-center">
          <Button variant="primary" onClick={() => navigate('/performance/accounts')}>
            <FontAwesomeIcon icon="arrow-left" className="me-1" />
            Volver a Cuentas
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3">
      <div className="pb-4">
        {/* Header */}
        <div className="mb-4">
          <Row className="align-items-center">
            <Col>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={() => navigate('/performance/accounts')}
                className="mb-2"
              >
                <FontAwesomeIcon icon="arrow-left" className="me-1" />
                Volver a Cuentas
              </Button>
              <h2 className="mb-2">
                <FontAwesomeIcon icon="credit-card" className="me-2 text-primary" />
                Gestión de Cuenta Publicitaria
              </h2>
              <h5 className="text-700 fw-semi-bold">
                {account?.AccountName || `Cuenta ID: ${accountId}`}
              </h5>
            </Col>
            <Col xs="auto">
              <Button 
                variant="primary" 
                onClick={() => navigate('/performance/order-insert')}
              >
                <FontAwesomeIcon icon="plus" className="me-1" />
                Nueva Orden
              </Button>
            </Col>
          </Row>
        </div>

        {/* Información de la Cuenta */}
        <Row className="mb-4">
          <Col md={8}>
            <Card className="h-100 shadow-sm">
              <Card.Header className="bg-light">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon="info-circle" className="me-2 text-primary" />
                  Información de la Cuenta
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <small className="text-muted">Nombre de la Cuenta</small>
                      <div className="fw-bold fs-5 text-primary">
                        {account?.AccountName || 'Sin información'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted">Tipo de Cuenta</small>
                      <div className="fw-semibold">
                        <Badge bg="secondary" className="fs-12">
                          {account?.AccountType || 'No especificado'}
                        </Badge>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <small className="text-muted">Cliente</small>
                      <div className="fw-bold text-success">
                        {client?.ClientName || 'Sin información'}
                      </div>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted">Identificador de Cuenta</small>
                      <div className="fw-semibold text-info">
                        {account?.AccountIdentifier || accountId}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={4}>
            <Card className="h-100 shadow-sm">
              <Card.Header className="bg-light">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon="chart-pie" className="me-2 text-success" />
                  Resumen
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="text-center">
                  <div className="mb-3">
                    <small className="text-muted">Total Órdenes</small>
                    <div className="fs-3 fw-bold text-primary">
                      {orders.length}
                    </div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Órdenes Activas</small>
                    <div className="fs-4 fw-bold text-success">
                      {getActiveOrdersCount()}
                    </div>
                  </div>
                  <div>
                    <small className="text-muted">Inversión Total</small>
                    <div className="fs-4 fw-bold text-warning">
                      {formatCurrency(getTotalInvestment())}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Órdenes de Inserción */}
        <Card className="shadow-sm">
          <Card.Header>
            <Row className="align-items-center">
              <Col>
                <h5 className="mb-0">
                  <FontAwesomeIcon icon="table" className="me-2 text-primary" />
                  Órdenes de Inserción ({orders.length})
                </h5>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            {orders.length > 0 ? (
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="border-0">ID</th>
                      <th className="border-0">Descripción</th>
                      <th className="border-0">Business Unit</th>
                      <th className="border-0">Período</th>
                      <th className="border-0">Inversión</th>
                      <th className="border-0">Estado</th>
                      <th className="border-0 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td className="align-middle">
                          <small className="fw-semibold text-primary">{order.id}</small>
                        </td>
                        <td className="align-middle">
                          <div className="text-truncate" style={{ maxWidth: '200px' }} title={order.description}>
                            {order.description}
                          </div>
                        </td>
                        <td className="align-middle">
                          <Badge bg="light" text="dark">
                            {order.businessUnit}
                          </Badge>
                        </td>
                        <td className="align-middle">
                          <small>
                            <div>{new Date(order.dateFrom).toLocaleDateString('es-ES')}</div>
                            <div className="text-muted">al {new Date(order.dateTo).toLocaleDateString('es-ES')}</div>
                          </small>
                        </td>
                        <td className="align-middle">
                          <div className="text-success fw-semibold">
                            {formatCurrency(order.grossInvestment)}
                          </div>
                          <div className="text-muted fs-11">
                            Neta: {formatCurrency(order.netInvestment)}
                          </div>
                        </td>
                        <td className="align-middle">
                          <Badge bg={order.isActive ? 'success' : 'secondary'}>
                            {order.isActive ? 'Activa' : 'Inactiva'}
                          </Badge>
                        </td>
                        <td className="align-middle text-center">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => navigate('/performance/order-insert')}
                            title="Ver/Editar orden"
                          >
                            <FontAwesomeIcon icon="eye" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-5">
                <FontAwesomeIcon icon="archive" className="fs-1 text-muted mb-3" />
                <h5 className="text-muted">No hay órdenes de inserción</h5>
                <p className="text-muted">Esta cuenta no tiene órdenes de inserción registradas.</p>
                <Button 
                  variant="primary" 
                  onClick={() => navigate('/performance/order-insert')}
                >
                  <FontAwesomeIcon icon="plus" className="me-1" />
                  Crear Primera Orden
                </Button>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default AccountDetailPage; 